import React from "react";
import { useNavigate } from "react-router-dom";

export default function FinanceHub() {
  const navigate = useNavigate();

  const sections = [
    {
      title: "Finance Operations",
      items: [
        {
          title: "Vouchers",
          description: "Manage, track, and approve office expenses, petty cash, and payment vouchers.",
          route: "/admin/vouchers",
          icon: "payments",
          color: "bg-blue-50 text-blue-600 border-blue-100",
          badge: "05 Pending",
          badgeColor: "bg-blue-100/50 text-blue-700",
        },
        {
          title: "Debit/Credit Notes",
          description: "Record account returns and ledger corrections using accounting notes.",
          route: "/admin/debit-credit-notes",
          icon: "credit_card",
          color: "bg-emerald-50 text-emerald-600 border-emerald-100",
          badge: "02 Drafts",
          badgeColor: "bg-emerald-100/50 text-emerald-700",
        },
        {
          title: "Accounts Monitor",
          description: "Audit action trails and system logs generated by the accounting department.",
          route: "/admin/accounts-monitor",
          icon: "monitor_heart",
          color: "bg-purple-50 text-purple-600 border-purple-100",
        },
      ],
    },
    {
      title: "Taxation & Reports",
      items: [
        {
          title: "GST Export & Reports",
          description: "Generate and download GST tax summary logs for financial accounting audits.",
          route: "/admin/gst-export-reports",
          icon: "download",
          color: "bg-indigo-50 text-indigo-600 border-indigo-100",
          badge: "Ready",
          badgeColor: "bg-indigo-100/50 text-indigo-700",
        },
      ],
    },
    {
      title: "Inventory & Master Settings",
      items: [
        {
          title: "Material Stock",
          description: "Track inventory, stock balances, item locations, and logs in the warehouse.",
          route: "/admin/material-stock",
          icon: "inventory_2",
          color: "bg-cyan-50 text-cyan-600 border-cyan-100",
          badge: "In Stock",
          badgeColor: "bg-cyan-100/50 text-cyan-700",
        },
        {
          title: "Product Mapping",
          description: "Link vendor and customer part numbers to standard product master catalogs.",
          route: "/admin/product-mapping",
          icon: "grid_view",
          color: "bg-rose-50 text-rose-600 border-rose-100",
        },
        {
          title: "Test Certificate Settings",
          description: "Manage parameters and signatures for generating official test certificates.",
          route: "/admin/test-certificate-settings",
          icon: "verified",
          color: "bg-amber-50 text-amber-600 border-amber-100",
        },
      ],
    },
  ];

  return (
    <div className="space-y-8 max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="border-b border-slate-100 pb-5">
        <span className="font-label-caps text-[11px] text-primary uppercase font-bold tracking-widest mb-1.5 block">
          ADMIN WORKSPACE
        </span>
        <h1 className="font-headline-lg text-3xl font-extrabold text-slate-900 tracking-tight">
          Accounts &amp; Finance Management
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Select an accounting ledger, tax reports, or inventory master setting to manage configurations.
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-10">
        {sections.map((section) => (
          <div key={section.title} className="space-y-4">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-l-4 border-primary pl-3">
              {section.title}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {section.items.map((item) => (
                <button
                  key={item.route}
                  onClick={() => navigate(item.route)}
                  className="group bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-primary/45 transition-all text-left flex items-start gap-4 active:scale-[0.98] w-full"
                >
                  {/* Left Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center border shrink-0 ${item.color}`}>
                    <span className="material-symbols-outlined text-2xl">{item.icon}</span>
                  </div>

                  {/* Text Contents */}
                  <div className="flex-1 min-w-0 pr-2">
                    <h3 className="text-base font-bold text-slate-800 flex items-center gap-2 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                      {item.description}
                    </p>
                  </div>

                  {/* Badge or Right Arrow */}
                  <div className="flex flex-col items-end justify-between self-stretch shrink-0">
                    {item.badge ? (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${item.badgeColor}`}>
                        {item.badge}
                      </span>
                    ) : (
                      <span className="material-symbols-outlined text-slate-300 text-sm group-hover:text-primary transition-colors group-hover:translate-x-0.5 duration-200">
                        arrow_forward
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
